import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { ApiServiceExternal } from '@frontend/sports/common/api-utils';
import { ConnectionConfig } from '@frontend/sports/common/client-config-data-access';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { Observable, catchError, concatMap, map, of } from 'rxjs';

import { ApiBaseSettingsProvider } from './api-settings.service';
import { CdsRequestOptions } from './cds-request-options';
import { encodeParams, urljoin } from './url.utils';

export interface CdsApiOptions {
    endpoint: string;
}

interface CdsApiContextQueryStringParameters {
    'x-bwin-accessid': string;
    'lang': string;
    'country': string;
    'userCountry': string;
    'userLocation': string;
    'restrictedCompetitionId'?: number;
    'subdivision'?: string;
    'shopTier'?: number;
}

@Injectable({ providedIn: 'root' })
export class BaseCdsApi {
    private apiServiceExternal = inject(ApiServiceExternal);
    private apiBaseSettings = inject(ApiBaseSettingsProvider);

    private frontendUrl: string;
    private apiUrl: string;
    private configured = false;
    private options: CdsApiOptions;
    private readonly logger: SportsRemoteLogger;
    private readonly cdsApiHeader: string = 'x-bwin-cds-api';
    private readonly _doc = inject(DOCUMENT);

    constructor() {
        const loggerFactory = inject(LoggerFactory);
        const connectionConfig = inject(ConnectionConfig);

        this.frontendUrl = connectionConfig.cdsFrontendUrl ?? connectionConfig.cdsUrlBase;
        this.apiUrl = connectionConfig.cdsApiUrl;
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        this.options = <CdsApiOptions>{};
        this.logger = loggerFactory.getLogger('BaseCdsApi');
    }

    configure(options: CdsApiOptions): BaseCdsApi {
        this.configured = true;
        this.options = { ...this.options, ...options };

        return this;
    }

    post<T, K = unknown>(resource: string, data?: K, options?: CdsRequestOptions): Observable<T | undefined> {
        if (!this.configured) {
            return of(undefined);
        }

        return this.prepareRequestUrl(resource, options).pipe(
            concatMap((url) =>
                this.apiServiceExternal
                    .post<HttpResponse<T>>(url, data, {
                        cache: false,
                        resolveWithFullResponse: true,
                        headers: this.getCdsApiHeader(),
                    })
                    .pipe(
                        map((response) => response.body as T),

                        catchError((err: unknown) => {
                            if (err instanceof HttpErrorResponse && err.status > 0) {
                                this.logger.error(err, `Post call to CDS endpoint: ${this.getUrl(resource)} failed`);
                            }

                            return of(undefined);
                        }),
                    ),
            ),
        );
    }

    get<T, K = unknown>(resource: string, data?: K, options?: CdsRequestOptions): Observable<T | undefined> {
        if (!this.configured) {
            return of(undefined);
        }

        return this.prepareRequestUrl(resource, options).pipe(
            concatMap((url) =>
                this.apiServiceExternal
                    .get<HttpResponse<T>>(url, data, {
                        resolveWithFullResponse: true,
                        transferCache: true,
                        headers: this.getCdsApiHeader(),
                    })
                    .pipe(
                        map((response) => response.body as T),
                        catchError((err: unknown) => {
                            if (err instanceof HttpErrorResponse && err.status > 0) {
                                this.logger.error(err, `Get call to CDS endpoint: ${this.getUrl(resource)} failed`);
                            }

                            return of(undefined); // Ventsi: I don't like the to return undefined on error it should be throwError but return to the prev behavior
                        }),
                    ),
            ),
        );
    }

    private prepareRequestUrl(resource: string, options?: CdsRequestOptions): Observable<string> {
        return this.getDefault(options).pipe(
            map((request) => {
                let url = this.getUrl(resource);
                url += url.includes('?') ? '&' : '?';

                return url + encodeParams(request);
            }),
        );
    }

    private getDefault(options?: CdsRequestOptions): Observable<CdsApiContextQueryStringParameters> {
        return this.apiBaseSettings.getSettingsWithGeolocation().pipe(
            map((settings) => {
                const { accessId, userLocation, ...rest } = settings;
                const defaultQueryStringParams: CdsApiContextQueryStringParameters = {
                    'x-bwin-accessid': accessId,
                    ...rest,
                    'userLocation': userLocation || '',
                };

                if (options?.restrictedCompetitionId) {
                    defaultQueryStringParams.restrictedCompetitionId = options.restrictedCompetitionId;
                }

                return defaultQueryStringParams;
            }),
        );
    }

    private getCdsApiHeader(): Record<string, string> {
        return this.apiUrl ? { [this.cdsApiHeader]: this.apiUrl } : {};
    }

    private getUrl(resource: string): string {
        const url = urljoin(this.frontendUrl, this.options.endpoint, resource);

        if (url.startsWith('/')) {
            return urljoin(`${this._doc.location.protocol}//${this._doc.location.host}`, url);
        }

        return url;
    }
}
